import { useCallback, useEffect, useRef, useState } from 'react'

export interface ElementSize {
  width: number
  height: number
}

export const useResizeElement = <T extends HTMLElement>(): {
  elementRef: (node: T | null) => void
  size: ElementSize
} => {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 })
  const observerRef = useRef<ResizeObserver | null>(null)
  const nodeRef = useRef<T | null>(null)

  const elementRef = useCallback((node: T | null) => {
    if (!node || node === nodeRef.current) {
      return
    }

    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    nodeRef.current = node

    observerRef.current = new ResizeObserver(([entry]) => {
      if (entry) {
        const { width, height } = entry.contentRect
        setSize((prev) => (prev.width !== width || prev.height !== height ? { width, height } : prev))
      }
    })

    observerRef.current.observe(node)
  }, [])

  useEffect(() => {
    if (nodeRef.current && observerRef.current) {
      observerRef.current.observe(nodeRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return { elementRef, size }
}
